import { useEffect, useRef, useState } from "react"
import { useSession } from "../../auth/session"
import { Link, useSearchParams } from "react-router-dom"
import Api from "../../api/api"

// Bridge page for BUY buttons on www.newfangledaudio.com:
//   www links to /buy?skuId=<uuid>; sign-in is enforced app-wide and
//   the login app brings the user back to this exact URL (returnTo).
// This page validates the sku, mints a purchaseId that uniquely
// identifies this purchase (it rides through the LemonSqueezy custom checkout
// data onto the order row, and through the redirect URL to /purchase/success,
// which polls for exactly that order), creates the checkout and navigates to
// it with location.replace — keeping /buy out of history so Back from the
// checkout doesn't mint another one.
export default function BuyPage() {
  const accessToken = useSession().accessToken ?? ""
  const [searchParams] = useSearchParams()
  const skuId = searchParams.get("skuId")
  const [error, setError] = useState<string | undefined>(undefined)
  const [attempt, setAttempt] = useState(0)
  const started = useRef(false)

  useEffect(() => {
    if (!skuId || !accessToken || started.current)
      return
    started.current = true;

    (async () => {
      const { sku } = await Api.getMySku({ skuId, accessToken })

      // BUY buttons sell the one-off purchase — pick the cheapest one-time offering
      const oneTimeOffering = sku.offerings
        .filter(o => o.paymentType === "one_time")
        .sort((a, b) => a.priceCents - b.priceCents)[0]
      if (!oneTimeOffering)
        throw new Error("This product is not sold in the store")

      const purchaseId = crypto.randomUUID()
      const { checkout } = await Api.makeSkuCheckout({
        skuId,
        offeringId: oneTimeOffering.id,
        redirectUrl: `${window.location.origin}/purchase/success?skuId=${skuId}&purchaseId=${purchaseId}`,
        upgrade: null,
        purchaseId,
        accessToken
      })
      window.location.replace(checkout.attributes.url)
    })().catch((err) => {
      console.error("Could not start checkout:", err)
      started.current = false
      setError(err instanceof Error ? err.message : "Something went wrong")
    })
  }, [skuId, accessToken, attempt])

  if (!skuId)
    return (
      <div className="page">
        <div className="empty-state-block">
          <span className="empty-state-icon">&#128722;</span>
          <span>This purchase link is incomplete</span>
          <span className="empty-state-hint">No product was specified — please use the Buy button on our website again, or browse the store</span>
          <Link to="/store" className="card-link" style={{ marginTop: 12 }}>Visit Store</Link>
        </div>
      </div>
    )

  if (error !== undefined)
    return (
      <div className="page">
        <div className="empty-state-block">
          <span className="empty-state-icon">&#9888;&#65039;</span>
          <span>We couldn't start your checkout</span>
          <span className="empty-state-hint">{error}</span>
          <div style={{ display: "flex", gap: 24, marginTop: 12 }}>
            <button
              className="card-link"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              onClick={() => { setError(undefined); setAttempt(attempt + 1) }}
            >
              Try again
            </button>
            <Link to="/store" className="card-link">Visit Store</Link>
          </div>
        </div>
      </div>
    )

  return (
    <div className="page">
      <p className="empty-state"><span className="spinner" /> Preparing your secure checkout…</p>
    </div>
  )
}
